<template>
  <component v-if="serverErrorResponseStatus === 404" :is="notFoundComponent"></component>

  <div class="container text-center" v-else-if="!jobLoaded">
    <img src="/static/loader.svg" height="80px" />
  </div>

  <div v-else class="container mt-4">

    <job-details-header :jobName="job.name" :jobStatus="job.status" :pauseInProgress="pauseInProgress"
      :creationDate="job.uploadDate"></job-details-header>
    <div class="row">
      <div class="col-lg">
        <job-details-summary :jobType="job.type" :jobStatus="job.status" :productLineName="job.productLineName"
          :batchId="job.batchId" :policyCasesStatus="job.policyCasesStatus"
          :rowsSuccess="job.completedRows - filteredErrors.length" :rowsTotal="job.totalRows"
          @update:selectedLines="handleSelectedLines"></job-details-summary>

        <job-details-progress :allRowsProcessed="allRowsProcessed" :jobType="job.type" :jobStatus="job.status"
          :completedPercent="completedPercent" :completedCount="completed.length" :errorPercent="errorPercent"
          :errorCount="job.errorCount" :skippedPercent="skippedPercent" :skippedCount="skipped.length"
          :pauseInProgress="pauseInProgress" :canceling="canceling" :atLeastOneSelected="selectedLines.length > 0"
          @pause-job="pauseJob" @start-job="startJob" @cancel-job="cancelJob">
        </job-details-progress>

      </div>
      <div class="col-lg-6 pl-0 ml-3" v-if="job.type.startsWith('IMPORT')">

        <job-details-card>
          <template slot="header">
            {{ 'WARNINGS' | translate }}
          </template>
          <template slot="body">
            <template v-for="warning in filteredWarnings">
              <li v-for="message in warning.messages" class="with-icon">
                <i class="fa fa-warning text-warning mr-2"></i> {{ message }}
              </li>
            </template>
          </template>
        </job-details-card>

      </div>
    </div>

    <job-details-buttons :jobName="job.name" :jobType="job.type" :jobStatus="job.status"
      :exportFileExists="job.exportFileExists">
    </job-details-buttons>

    <template
      v-if="(this.job.type === 'CLOSE' || this.job.type === 'DELETE_CASES' || this.job.type === 'EXPORT' || this.job.type === 'DELETE_CUSTOMERS') && this.errors && this.errors.length && this.errors.length > 0">
      <job-details-card :additional-classes="'style-danger h-100'">
        <template slot="header">
          {{ 'ERRORS' | translate }}
        </template>
        <template slot="body">
          <template v-for="error in this.errors">
            <li v-for="message in error.messages" class="with-icon">
              <i class="fa fa-exclamation-circle text-danger mr-2"></i>
              {{ message }}
            </li>
          </template>
        </template>
      </job-details-card>
    </template>

    <div class="row mt-4"
      v-if="job.type.startsWith('IMPORT') || job.type === 'RECALC' || job.type === 'RECALC_CASE_IDS' || job.type === 'CLOSE_CASE_IDS' || job.type === 'DELETE_CASE_IDS'">
      <div class="col pr-0">

        <job-details-card :additional-classes="'style-success columns-3 h-100'">
          <template slot="header">
            {{ 'COMPLETED' | translate }}
            <span class="badge py-1 ml-1" :class="[completed.length > 0 ? 'badge-success' : 'badge-light']">
              {{ completed.length }}
            </span>
          </template>
          <template slot="body">
            <li v-for="row in completed" class="text-center">
              {{ 'LINE_NR' | translate }} {{ row.lineNumber }} -
              <template v-if="job.type !== 'DELETE_CASE_IDS'">
                <router-link :to="'/case/' + row.caseId" class="font-weight-bold one-line-ellipsis">
                  {{ row.policycaseHeaderId }}
                </router-link>
              </template>

              <template v-else>
                <span class="font-weight-bold one-line-ellipsis">
                  {{ row.policycaseHeaderId }}
                </span>
              </template>
            </li>
          </template>
        </job-details-card>

      </div>
      <div class="col pl-0">

        <job-details-card :additional-classes="'style-danger h-100'">
          <template slot="header">
            {{ 'ERRORS' | translate }}
            <span class="badge py-1 ml-1" :class="[filteredErrors.length > 0 ? 'badge-danger' : 'badge-light']">
              {{ filteredErrors.length }}
            </span>
          </template>
          <template slot="body">
            <template v-for="error in filteredErrors">
              <li v-for="message in error.messages" class="with-icon">
                <i class="fa fa-exclamation-circle text-danger mr-2"></i>
                {{ message }}
                (<strong>{{ 'LINE_NR' | translate }} {{ error.lineNumber }}</strong>)
              </li>
            </template>
          </template>
        </job-details-card>

      </div>
    </div>
  </div>
</template>

<script>
import RestService from '../../service/RestService.js';
import GeneralConfigService from '../../service/GeneralConfigService';
import NotFound from '../NotFound';
import JobDetailsHeader from './JobDetailsHeader';
import JobDetailsSummary from './JobDetailsSummary';
import JobDetailsProgress from './JobDetailsProgress';
import JobDetailsCard from './JobDetailsCard';
import JobDetailsButtons from './JobDetailsButtons';

export default {
  components: {JobDetailsHeader, JobDetailsSummary, JobDetailsProgress, JobDetailsCard, JobDetailsButtons},
  props: ['jobId'],
  data: function() {
    return {
      rest: new RestService(),
      notFoundComponent: NotFound,
      serverErrorResponseStatus: null,
      jobLoaded: false,
      job: {},
      oldJobState: {},
      errors: [],
      completed: [],
      skipped: [],
      pauseInProgress: false,
      isJobRunning: false,
      polling: false,
      canceling: false,
      extraPool: 0,
      pollingInterval: null,
      pauseInProgressInterval: null,
      POLLING_PERIOD_MS: 2000,
      JOB_STATUS_UPDATE_TIME: 600,
      warningErrorTypes: ['MISSING_REQUIRED_COLUMN', 'MISSING_OPTIONAL_COLUMN', 'COLUMN_WARNING'],
      selectedLines: []
    }
  },
  beforeMount: function() {
    this.init();
  },
  beforeRouteLeave (to, from, next) {
    clearInterval(this.pollingInterval);
    clearInterval(this.pauseInProgressInterval);
    next();
  },
  computed: {
    filteredErrors: function() {
      if (this.job.type === 'CLOSE' || this.job.type === 'DELETE_CASES' || this.job.type === 'EXPORT' || this.job.type === 'DELETE_CUSTOMERS') {
        if (this.job.status === 'FAILED') {
          return this.createDummyArrayOf(this.job.totalRows);
        }
      }
      return this.errors.filter((el) => {
        return !this.warningErrorTypes.includes(el.errorType);
      });
    },
    filteredWarnings: function() {
      return this.errors.filter((el) => {
        return this.warningErrorTypes.includes(el.errorType);
      });
    },
    allRowsProcessed: function() {
      if (this.job.status === 'RUNNING' || this.job.status === 'PENDING') {
        if (this.job.totalRows === 0) {
          return false;
        }
      }
      return (this.completed.length + (this.filteredErrors.length !== 0 ? this.filteredErrors.length : this.skipped.length) === this.job.totalRows);
    },
    completedPercent: function() {
      return this.completed.length / this.job.totalRows * 100;
    },
    errorPercent: function() {
      return this.filteredErrors.length / this.job.totalRows * 100;
    },
    skippedPercent: function() {
      return this.skipped.length / this.job.totalRows * 100;
    }
  },
  methods: {
    init: function() {
      this.getJob();
    },
    poll: function() {
      this.getJob();
      if (this.allRowsProcessed) {
        this.polling = false;
        this.pauseInProgress = false;
      }
    },
    checkForRunningRowAndGetJob: function () {
      this.rest.isSomeJobRowStillRunning(this.job.rowId).then((response) => {
        this.getJob();
        if (response.data !== true || this.allRowsProcessed) {
          this.pauseInProgress = false;
          setTimeout(() => {
            this.getJob();
          }, this.JOB_STATUS_UPDATE_TIME);
        }
      }).catch((err) => {
        if (err.response) this.serverErrorResponseStatus = err.response.status;
        this.$notify({
          type: 'alert-danger',
          duration: -1,
          text: this.$options.filters.translate('ERROR_GETTING_JOB_DETAILS', 'msg')
        });
        this.$log.error(err);
      });
    },
    getJob: function() {
      this.rest.getJob(this.jobId)
        .then((res) => {
          this.job = res.data;
          // AA-709 The "total antal" for CLOSE DELETE and EXPORT should be based on the job status - 0 or totalRows
          // (they do not use completedRows in the same way as IMPORT and RECALCULATE). This should be a temporary solution.
          if (this.job.type === 'CLOSE' || this.job.type === 'DELETE_CASES' || this.job.type === 'EXPORT' || this.job.type === 'DELETE_CUSTOMERS') {
            if (this.job.status === 'COMPLETE') {
              this.job.completedRows = this.job.totalRows;
              this.completed = this.createDummyArrayOf(this.job.totalRows);
              this.errors = [];
            } else if (this.job.status === 'RUNNING' || this.job.status === 'PENDING') {
              this.completed = [];
              this.errors = [];
            } else {
              this.job.completedRows = this.job.totalRows;
              this.job.errorCount = this.job.totalRows;
              this.completed = [];
              this.getErrors();
            }
          } else {
            this.getCompleted();
            this.getSkipped();
            this.getErrors();
          }
          this.jobLoaded = true;
          if (this.job.status === 'RUNNING' || this.job.status === 'PENDING') this.polling = true;
        }).catch(function(err) {
          if (err.response) this.serverErrorResponseStatus = err.response.status;
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_GETTING_JOB_DETAILS', 'msg')
          });
          this.$log.error(err);
        });
    },
    createDummyArrayOf: function(size) {
      let outArray = [];
      for (let i = 0; i < size; i++) {
        outArray.push(i);
      }
      return outArray;
    },
    getErrors: function() {
      this.rest.getErrors(this.jobId)
        .then((res) => {
          let errors = res.data;
          this.errors = errors.sort(function(a, b) {
            if (a.lineNumber > b.lineNumber) { return 1; }
            if (a.lineNumber < b.lineNumber) { return -1; }
            return 0;
          });
        }).catch(function(err) {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_GETTING_JOB_DETAILS', 'msg')
          });
          this.$log.error(err);
        });
    },
    async getCompleted() {
      try {
        const res = await this.rest.getCompleted(this.jobId);

        // Fetch caseId for each row
        const completedWithCaseIds = await Promise.all(
          res.data.map(async (row) => {
            const caseRes = await this.rest.getCompletedCaseId(row.policycaseHeaderId);
            return {
              ...row,
              caseId: caseRes.data.value === undefined ? caseRes.data : caseRes.data.value
            };
          })
        );

        this.completed = completedWithCaseIds;
      } catch (err) {
        this.$notify({
          type: 'alert-danger',
          duration: -1,
          text: this.$options.filters.translate('ERROR_GETTING_JOB_DETAILS', 'msg')
        });
        this.$log.error(err);
      }
    },
    getSkipped() {
      this.rest.getSkipped(this.jobId)
        .then((res) => {
          this.skipped = res.data;
        }).catch(function(err) {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_GETTING_JOB_DETAILS', 'msg')
          });
          this.$log.error(err);
        });
    },
    startJob() {
      this.pauseInProgress = false;
      this.job.status = 'PENDING';
      if (this.job.type.startsWith('IMPORT')) GeneralConfigService.forceUpdatePolicyCaseBatchIds = true;
      this.rest.changeJobStatus(this.jobId, 'PENDING', this.selectedLines)
        .then(() => {
          this.polling = true;
          this.poll();
        }).catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_STARTING_JOB', 'msg')
          });
          this.$log.error(err);
        });
    },
    pauseJob() {
      this.pauseInProgress = true;
      this.rest.changeJobStatus(this.jobId, 'PAUSED', this.selectedLines)
        .then(() => {
          this.polling = false; // this will stop the pooling but we still need to pool several extra times, so that currently processed rows can become done and visualized
        }).catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_PAUSING_JOB', 'msg')
          });
          this.$log.error(err);
        });
    },
    async cancelJob() {
      this.canceling = true;
      await this.rest.cancelJob(this.jobId)
        .catch((err) => {
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: this.$options.filters.translate('ERROR_CANCELING_JOB', 'msg')
          });
          this.$log.error(err);
        });
      await this.getJob();
      this.canceling = false;
    },
    handleSelectedLines(selectedLines) {
      this.selectedLines = selectedLines.map(line => line);
    }
  },
  watch: {
    polling: function() {
      if (this.polling) {
        this.pollingInterval = setInterval(() => {
          this.poll();
        }, this.POLLING_PERIOD_MS);
      } else {
        clearInterval(this.pollingInterval);
      }
    },
    pauseInProgress: function(val) {
      if (val) {
        this.pauseInProgressInterval = setInterval(() => {
          this.checkForRunningRowAndGetJob()
        }, this.POLLING_PERIOD_MS);
      } else {
        clearInterval(this.pauseInProgressInterval);
      }
    }
  }
}
</script>

<style scoped>
  .container {
    font-size: 14px;
  }
  .one-line-ellipsis {
  display: inline-block;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

/* Medium screens and up */
@media (min-width: 768px) {
  .one-line-ellipsis {
    max-width: 220px;
  }
}

/* Large screens and up */
@media (min-width: 1200px) {
  .one-line-ellipsis {
    max-width: 320px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/job/JobDetails.vue